import { ChangeDetectionStrategy, Component, inject, input, signal } from '@angular/core';
import { CooldownService } from '@gbr-monorepo/frontend';
import { DialogService, NotificationService, ShButtonComponent, ShSpinnerComponent } from '@gbr-monorepo/gbr-shui';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EMPTY, catchError, filter, finalize, switchMap, take, tap } from 'rxjs';
import { BasicDialogComponent } from '../../../../shared/components/basic-dialog/basic-dialog.component';
import { SeasonExportService } from '../../../services/season-export.service';

@Component({
    selector: 'app-season-details-export-application-button',
    templateUrl: 'season-details-export-application-button.component.html',
    imports: [TranslateModule, ShButtonComponent, ShSpinnerComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SeasonDetailsExportApplicationButtonComponent {
    protected readonly seasonExportService = inject(SeasonExportService);
    protected readonly cooldownService = inject(CooldownService);

    private readonly dialog = inject(DialogService);
    private readonly notificationService = inject(NotificationService);
    private readonly translate = inject(TranslateService);

    public readonly seasonId = input.required<string>();

    protected readonly isExporting = signal(false);

    protected exportApplications(): void {
        const seasonId = this.seasonId();

        if (this.isExporting() || this.cooldownService.isCooldownActive(seasonId)) {
            return;
        }

        this.dialog
            .open(BasicDialogComponent, {
                width: '800px',
                title: 'seasons.season-details.export-applications.confirmation.title',
                data: {
                    message: 'seasons.season-details.export-applications.confirmation.message',
                    reject: 'seasons.season-details.export-applications.confirmation.reject',
                    confirm: 'seasons.season-details.export-applications.confirmation.confirm'
                }
            })
            .afterClosed.pipe(
                take(1),
                filter(confirmed => confirmed === 'Confirmed'),
                filter(() => !this.isExporting() && !this.cooldownService.isCooldownActive(seasonId)),
                tap(() => this.isExporting.set(true)),
                switchMap(() => this.seasonExportService.exportApplications(seasonId)),
                tap(() => this.cooldownService.startCooldown(seasonId, { minutes: 5 })),
                catchError(() => {
                    this.notificationService.show({
                        title: this.translate.instant('seasons.season-details.export-applications.error.title'),
                        description: this.translate.instant(
                            'seasons.season-details.export-applications.error.description'
                        ),
                        duration: 5000,
                        type: 'error'
                    });

                    return EMPTY;
                }),
                finalize(() => this.isExporting.set(false))
            )
            .subscribe();
    }
}
